import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Building2, MapPin, Link, Users, Plus, Eye, Share, MoreHorizontal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { mockCompanies, mockJobs } from '../data/mock';
import { toast } from '../hooks/use-toast';

const Company = () => {
  const { id } = useParams();
  const [isFollowing, setIsFollowing] = useState(false);
  
  // Mock company data - in real app, this would come from API
  const company = {
    id: id,
    name: "TrainingCorp Solutions",
    industry: "Corporate Training & Development",
    size: "500-1000 employees",
    location: "New York, NY",
    logo: "https://images.unsplash.com/photo-1560472355-536de3962603?w=100&h=100&fit=crop",
    cover: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=200&fit=crop",
    followers: 12500,
    description: "Leading provider of corporate training solutions worldwide. We specialize in leadership development, technical skills training, and organizational transformation programs. Our mission is to empower professionals and organizations to achieve their full potential through innovative learning experiences.",
    website: "https://trainingcorp.com",
    founded: "2010",
    specialties: ["Leadership Development", "Technical Training", "E-Learning Solutions", "Corporate Workshops", "Performance Coaching"],
    employees: [
      {
        id: "1",
        name: "Sarah Johnson",
        title: "Head of Training Operations",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=50&h=50&fit=crop&crop=face"
      },
      {
        id: "2",
        name: "Mike Rodriguez",
        title: "Senior Training Specialist",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face"
      }
    ]
  };

  const companyJobs = mockJobs.filter(job => job.company === "Microsoft" || job.company === "Amazon");

  const handleFollow = () => {
    setIsFollowing(!isFollowing);
    toast({
      title: isFollowing ? "Unfollowed company" : "Following company!",
      description: isFollowing ? 
        `You are no longer following ${company.name}` : 
        `You are now following ${company.name}`,
    });
  };

  return (
    <div className="space-y-6">
      {/* Company Header */}
      <Card>
        <div className="relative">
          <div 
            className="h-32 rounded-t-lg bg-cover bg-center"
            style={{ backgroundImage: `url(${company.cover})` }}
          >
            <div className="absolute inset-0 bg-black/20 rounded-t-lg"></div>
          </div>
          <div className="absolute -bottom-12 left-6">
            <Avatar className="w-24 h-24 border-4 border-white">
              <AvatarImage src={company.logo} alt={company.name} />
              <AvatarFallback className="text-xl">{company.name.charAt(0)}</AvatarFallback>
            </Avatar>
          </div>
        </div>
        <CardContent className="pt-16 pb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <h1 className="text-2xl font-bold">{company.name}</h1>
                <Badge variant="outline" className="text-blue-600">
                  <Building2 className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              </div>
              <p className="text-gray-700 mb-2">{company.industry}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  {company.location}
                </div>
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  {company.size}
                </div>
                <div className="flex items-center">
                  <Link className="w-4 h-4 mr-1" />
                  <a href={company.website} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Website
                  </a>
                </div>
              </div>
              <div className="text-sm text-gray-600 mb-4">
                {company.followers.toLocaleString()} followers
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm">
                <Eye className="w-4 h-4 mr-2" />
                Visit website
              </Button>
              <Button 
                onClick={handleFollow}
                variant={isFollowing ? "outline" : "default"}
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                {isFollowing ? "Following" : "Follow"}
              </Button>
              <Button variant="ghost" size="sm">
                <Share className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Info */}
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">
          {/* About */}
          <Card>
            <CardHeader>
              <CardTitle>About</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">{company.description}</p>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Website</h4>
                  <a href={company.website} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    {company.website}
                  </a>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Industry</h4>
                  <p className="text-gray-700">{company.industry}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Company size</h4>
                  <p className="text-gray-700">{company.size}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Founded</h4>
                  <p className="text-gray-700">{company.founded}</p>
                </div>
              </div>
              
              <Separator className="my-4" />
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Specialties</h4>
                <div className="flex flex-wrap gap-2">
                  {company.specialties.map((specialty, index) => (
                    <Badge key={index} variant="secondary">
                      {specialty}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Jobs */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  Jobs at {company.name}
                  <Badge className="ml-2">{companyJobs.length}</Badge>
                </CardTitle>
                <Button variant="outline" size="sm">
                  See all jobs
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {companyJobs.slice(0, 2).map((job) => (
                <div key={job.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <h4 className="font-semibold text-lg hover:text-blue-600 cursor-pointer">
                    {job.title}
                  </h4>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                    <span>{job.location}</span>
                    <span>•</span>
                    <span>{job.type}</span>
                    <span>•</span>
                    <span>{job.posted}</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-2">{job.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-sm text-gray-500">
                      {job.applicants} applicants
                    </div>
                    <Button size="sm">Apply now</Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Employees */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Employees at {company.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {company.employees.map((employee) => (
                <div key={employee.id} className="flex items-center space-x-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={employee.avatar} alt={employee.name} />
                    <AvatarFallback>{employee.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm hover:text-blue-600 cursor-pointer truncate">
                      {employee.name}
                    </h4>
                    <p className="text-xs text-gray-600 truncate">{employee.title}</p>
                  </div>
                  <Button variant="outline" size="sm" className="text-xs">
                    Connect
                  </Button>
                </div>
              ))}
              <Button variant="ghost" className="w-full text-sm text-blue-600">
                View all employees
              </Button>
            </CardContent>
          </Card>

          {/* Similar Companies */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Similar companies</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {mockCompanies.slice(0, 2).map((similarCompany) => (
                <div key={similarCompany.id} className="flex items-center space-x-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={similarCompany.logo} alt={similarCompany.name} />
                    <AvatarFallback>{similarCompany.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm hover:text-blue-600 cursor-pointer truncate">
                      {similarCompany.name}
                    </h4>
                    <p className="text-xs text-gray-600 truncate">{similarCompany.industry}</p>
                    <p className="text-xs text-gray-500">{similarCompany.followers.toLocaleString()} followers</p>
                  </div>
                  <Button variant="outline" size="sm" className="text-xs">
                    <Plus className="w-3 h-3 mr-1" />
                    Follow
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Company;